import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "messages", "input", "form", "status"]
  static values = {
    token: String,
    pageEventId: Number,
    createUrl: String,
    updateUrl: String
  }

  connect() {
    this.open = false
    this.boundToggle = this.toggleFromEvent.bind(this)
    this.boundOpen = this.openFromEvent.bind(this)
    document.addEventListener("ask-ai:toggle", this.boundToggle)
    document.addEventListener("ask-ai:open", this.boundOpen)
    if (this.element.dataset.askAiAutostart === "true") {
      this.show()
    }
  }

  disconnect() {
    document.removeEventListener("ask-ai:toggle", this.boundToggle)
    document.removeEventListener("ask-ai:open", this.boundOpen)
    document.body.classList.remove("ask-ai-open")
  }

  toggleFromEvent(event) {
    event.preventDefault?.()
    this.toggle()
  }

  openFromEvent(event) {
    event.preventDefault?.()
    this.show()
  }

  toggle(event) {
    event?.preventDefault()
    this.open ? this.hide() : this.show()
  }

  show() {
    this.open = true
    this.panelTarget.classList.add("is-open")
    this.panelTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("ask-ai-open")
    this.inputTarget.focus()
    if (!this.tokenValue) {
      this.startConversation()
    }
  }

  hide() {
    this.open = false
    this.panelTarget.classList.remove("is-open")
    this.panelTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("ask-ai-open")
  }

  async send(event) {
    event.preventDefault()
    const message = this.inputTarget.value.trim()
    if (!message) return

    this.appendMessage("user", message)
    this.inputTarget.value = ""
    this.setStatus("Searching published event information…")

    try {
      if (!this.tokenValue) {
        await this.startConversation()
      }

      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({
          token: this.tokenValue,
          message,
          page_event_id: this.hasPageEventIdValue ? this.pageEventIdValue : null
        })
      })

      const payload = await response.json()
      if (!response.ok) {
        this.appendMessage("assistant", payload.error || "I couldn't complete that search.")
        return
      }

      this.appendMessage("assistant", payload.text, payload.citations || [])
    } catch (_error) {
      this.appendMessage("assistant", "I couldn't complete that search. Please try again.")
    } finally {
      this.setStatus("")
    }
  }

  async startConversation() {
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({
        page_event_id: this.hasPageEventIdValue ? this.pageEventIdValue : null
      })
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload.error || "Unable to start Ask AI")

    this.tokenValue = payload.token
    if (this.messagesTarget.children.length === 0) {
      this.appendMessage("assistant", payload.text)
    }
  }

  appendMessage(role, text, citations = []) {
    const wrapper = document.createElement("div")
    wrapper.className = `ask-ai-message ask-ai-message--${role}`

    const bubble = document.createElement("div")
    bubble.className = "ask-ai-message__bubble"
    text.toString().split("\n").forEach((line) => {
      const paragraph = document.createElement("p")
      paragraph.textContent = line || " "
      bubble.appendChild(paragraph)
    })
    wrapper.appendChild(bubble)

    if (citations.length > 0) {
      const list = document.createElement("ul")
      list.className = "ask-ai-citations"
      citations.forEach((citation) => {
        const item = document.createElement("li")
        const link = document.createElement("a")
        link.href = citation.url
        const suffix = citation.historical ? " (past event)" : ""
        link.textContent = `${citation.title || citation.event_title || "View record"}${suffix}`
        item.appendChild(link)
        list.appendChild(item)
      })
      wrapper.appendChild(list)
    }

    this.messagesTarget.appendChild(wrapper)
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content
    }
  }
}
